import styled from '@emotion/styled';
import queryString from 'query-string';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, RouteComponentProps } from 'react-router-dom';

import { config } from 'src/config/config';
import { navigationService } from 'src/config/rootService';
import { RouteGuard } from 'src/routing/RouteGuard';
import { Button, HeadingText30, HeadingText50 } from '@modernhealth/vital-components';
import { color, mediaQueries } from 'src/shared/styles/variables';

import { processSessionUuid, validUuid } from 'src/shared/helpers/uuidValidator';
import { VideoSessionGuard } from '../components/VideoSessionGuard';

import appStoreButton from 'src/public/img/btn-appstore.svg';
import googlePlayButton from 'src/public/img/btn-google-play.svg';
import closeIcon from 'src/public/img/icon-close-gray.svg';
import img from 'src/public/img/video-session.svg';
import { useMediaQuery } from '@react-hook/media-query';
import { mediaQuery } from '@modernhealth/vital-foundation';
import { captureException } from 'src/shared/helpers/captureException';

const Wrapper = styled.div({
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(77, 93, 110, 0.8)',
    [mediaQueries.sm]: {
        alignItems: 'flex-start',
        backgroundColor: color.white,
    },
});

const InnerContainer = styled.div({
    position: 'relative',
    padding: '70px 40px',
    backgroundColor: color.white,
    borderRadius: 8,
    [mediaQueries.sm]: {
        padding: '30px 15px',
    },
});

const CloseIcon = styled.img({
    position: 'absolute',
    top: 10,
    right: 10,
    padding: 20,
    transform: 'scale(1.5)',
    cursor: 'pointer',
});

const Image = styled.img({
    display: 'block',
    margin: '0 auto',
});

const MobileContentWrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const ButtonsWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    marginTop: '2em',
});

const StoreButton = styled.img({
    cursor: 'pointer',
    [mediaQueries.sm]: {
        width: 150,
    },
});

interface MatchParams {
    uuid: string;
}

const isValidSessionUuid = (uuid: string | string[] | null): boolean => {
    if (!validUuid(uuid)) {
        captureException(new Error('Invalid Session UUID on video session redirect'), {
            error_info: 'Failed to redirect to video session',
            sessionUuid: uuid,
        });
        return false;
    }
    return true;
};

const VideoSessionPage: React.FC<RouteComponentProps<MatchParams>> = ({ match, location }) => {
    const { t } = useTranslation();

    const smallScreenDetected = useMediaQuery(mediaQuery.maxWidth('sm', true));
    const TextComponent = smallScreenDetected ? HeadingText30 : HeadingText50;

    const urlParams = queryString.parse(location.search);
    let sessionUuid;

    if (urlParams && urlParams?.session) {
        const path = location.search;
        sessionUuid = path.split('=')[1];
        sessionUuid = processSessionUuid(sessionUuid);
        if (!isValidSessionUuid(sessionUuid)) {
            return <Redirect to="/dashboard" />;
        }
        return <Redirect to={`/video-session/${sessionUuid}`} />;
    }

    if (urlParams && urlParams?.uuid) {
        sessionUuid = urlParams.uuid;
        sessionUuid = processSessionUuid(sessionUuid);
        if (!isValidSessionUuid(sessionUuid)) {
            return <Redirect to="/dashboard" />;
        }
        return <Redirect to={`/video-session/${sessionUuid}`} />;
    }

    const mobileBrowserDetected = /Mobi|Android/i.test(navigator.userAgent);
    const sessionUrl = `modernhealth://authenticated/video-session/${match.params.uuid}`;
    if (!isValidSessionUuid(match.params.uuid)) {
        sessionUuid = match.params.uuid;
        sessionUuid = processSessionUuid(sessionUuid);
        return <Redirect to="/dashboard" />;
    }

    const handleStoreButtonClick = (link: string) => () => {
        window.open(link, '_blank');
    };

    if (!mobileBrowserDetected) {
        return (
            <RouteGuard
                path="/video-session/:uuid?"
                analytics={{ pageName: 'video-session' }}
                component={VideoSessionGuard}
                secured
            />
        );
    }

    return (
        <Wrapper>
            <InnerContainer>
                <CloseIcon
                    src={closeIcon}
                    onClick={() => navigationService.navigate('/dashboard')}
                />
                <Image src={img} />

                <MobileContentWrapper>
                    <TextComponent centered>{t('videoSessions:video_page.title')}.</TextComponent>
                    <Button
                        onClick={() => window.location.replace(sessionUrl)}
                        size="large"
                        variant="primary"
                    >
                        {t('videoSessions:video_page.open_in_app')}
                    </Button>
                    <TextComponent centered marginTop={20}>
                        {t('videoSessions:video_page.no_app')}
                    </TextComponent>
                    <ButtonsWrapper>
                        <StoreButton
                            src={appStoreButton}
                            style={{ marginRight: 15 }}
                            onClick={handleStoreButtonClick(config.iOSAppStoreURL)}
                        />
                        <StoreButton
                            src={googlePlayButton}
                            onClick={handleStoreButtonClick(config.AndroidGooglePlayStoreURL)}
                        />
                    </ButtonsWrapper>
                </MobileContentWrapper>
            </InnerContainer>
        </Wrapper>
    );
};

export default VideoSessionPage;
